import React, { useMemo, useState } from "react";
import "./ReconciliationTable.css";
import ReconciliationFilter from "./ReconciliationFilter/ReconciliationFilter";
import { StatementReconciliationItem } from "../../../Types/Invoice";

interface ReconciliationTableProps {
  items: StatementReconciliationItem[];
}

const statusClass = (status: string) => {
  if (status.toLowerCase() === "missing") return "missing";
  if (status.toLowerCase() === "matched") return "matched";
  return "invoiced";
};

const formatDate = (value: string | null) => {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString(undefined, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
};

const formatAmount = (value: number | null) =>
  value == null
    ? "—"
    : value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const ReconciliationTable: React.FC<ReconciliationTableProps> = ({ items }) => {
  const [activeFilter, setActiveFilter] = useState("All");

  const counts = useMemo(() => ({
    all: items.length,
    matched: items.filter((item) => item.status.toLowerCase() === "matched").length,
    missing: items.filter((item) => item.status.toLowerCase() === "missing").length,
  }), [items]);
  const filtered = activeFilter === "All"
    ? items
    : items.filter((item) => item.status.toLowerCase() === activeFilter.toLowerCase());

  return (
    <div className="recon-container">
      <div className="recon-header">
        <span className="recon-title">Reconciliation</span>
        <ReconciliationFilter
          activeFilter={activeFilter}
          onChange={setActiveFilter}
          counts={counts}
        />
      </div>

      <div className="recon-table-wrap">
        <table className="recon-table">
          <thead>
            <tr>
              <th>Status</th>
              <th>Date</th>
              <th>Due date</th>
              <th>Source(Supplier Name)</th>
              <th>Reference</th>
              <th>Type</th>
              <th>Total amount</th>
              <th>VAT amount</th>
              <th>	Net amount</th>
              <th>	Due amount</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.supplierStatementItemId}>
                <td>
                  <span className={`recon-badge ${statusClass(row.status)}`}>{row.status}</span>
                </td>
                <td>{formatDate(row.transactionDate)}</td>
                <td>{formatDate(row.dueDate)}</td>
                <td>{row.source}</td>
                <td>{row.transactionReference}</td>
                <td>{row.type}</td>
                <td>{formatAmount(row.totalAmount)}</td>
                <td>{formatAmount(row.vatAmount)}</td>
                <td>{formatAmount(row.netAmount)}</td>
                <td>{formatAmount(row.dueAmount)}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={10} className="recon-empty">No reconciliation items found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReconciliationTable;
